/**
 * User domain types
 * @module @orchard9ai/types/domain/user
 */

/**
 * User role enumeration
 */
export enum UserRole {
  Admin = 'ADMIN',
  User = 'USER',
  Guest = 'GUEST',
  Moderator = 'MODERATOR',
}

/**
 * User status enumeration
 */
export enum UserStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Suspended = 'SUSPENDED',
  Deleted = 'DELETED',
}

/**
 * Base user properties shared across all user types
 */
export interface BaseUser {
  /** Unique identifier */
  readonly id: string;
  /** User's email address */
  email: string;
  /** User's display name */
  displayName: string;
  /** User's role in the system */
  role: UserRole;
  /** User's current status */
  status: UserStatus;
  /** ISO 8601 timestamp of user creation */
  createdAt: string;
  /** ISO 8601 timestamp of last update */
  updatedAt: string;
}

/**
 * Authenticated user with additional auth properties
 */
export interface AuthenticatedUser extends BaseUser {
  /** JWT access token */
  accessToken: string;
  /** Refresh token for obtaining new access tokens */
  refreshToken?: string;
  /** Token expiration time in seconds */
  expiresIn: number;
  /** User's permissions */
  permissions: string[];
}

/**
 * User profile information
 */
export interface UserProfile {
  /** Reference to user ID */
  userId: string;
  /** User's first name */
  firstName?: string;
  /** User's last name */
  lastName?: string;
  /** User's avatar URL */
  avatarUrl?: string;
  /** User's bio or description */
  bio?: string;
  /** User's location */
  location?: string;
  /** User's timezone */
  timezone?: string;
  /** User's preferred language */
  language?: string;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Complete user model combining base user and profile
 */
export interface User extends BaseUser {
  /** User's profile information */
  profile?: UserProfile;
}

/**
 * User creation input
 */
export interface CreateUserInput {
  /** User's email address */
  email: string;
  /** User's password */
  password: string;
  /** User's display name */
  displayName: string;
  /** Optional role assignment */
  role?: UserRole;
  /** Optional profile data */
  profile?: Partial<UserProfile>;
}

/**
 * User update input
 */
export interface UpdateUserInput {
  /** Updated email address */
  email?: string;
  /** Updated display name */
  displayName?: string;
  /** Updated role */
  role?: UserRole;
  /** Updated status */
  status?: UserStatus;
  /** Updated profile data */
  profile?: Partial<UserProfile>;
}

/**
 * User authentication credentials
 */
export interface UserCredentials {
  /** User's email or username */
  identifier: string;
  /** User's password */
  password: string;
  /** Remember me flag for extended sessions */
  rememberMe?: boolean;
}

/**
 * User session information
 */
export interface UserSession {
  /** Session ID */
  sessionId: string;
  /** User ID */
  userId: string;
  /** IP address */
  ipAddress: string;
  /** User agent string */
  userAgent: string;
  /** Session start time */
  startedAt: string;
  /** Last activity time */
  lastActivityAt: string;
  /** Session expiration time */
  expiresAt: string;
}
